import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { AppError } from '@hookmyapp/observability/errors';
import type { WorkspacePublicId } from '@hookmyapp/shared';
import { emit } from '@/lib/analytics';
import { captureException } from '@/lib/sentry';
import { workspaceUrl } from '@/lib/workspace-url';
import { useWorkspace } from '@/contexts/workspace-context';
import { useFacebookLoginToken } from '@/components/use-facebook-login-listeners';
import {
  clearFacebookConnectIntent,
  fetchFacebookPages,
  isEligibleFacebookPage,
  readFacebookConnectIntent,
  readFacebookReconnectPageId,
  startFacebookConnect,
  submitFacebookSignup,
  type FacebookPageOption,
} from '@/lib/facebook-connect';

type FetchWithAuth = (path: string, options?: RequestInit) => Promise<Response>;

export type FacebookConnectStep = 'idle' | 'awaitingToken' | 'loadingPages' | 'picking' | 'submitting';

/**
 * AIT-621 Facebook Page connect, two round trips: the Facebook dialog relays a
 * short-lived user token back (popup postMessage or localStorage), the hook
 * lists the customer's Pages, the customer picks one, the backend creates the
 * channel pair. Exactly one eligible Page skips the picker.
 *
 * `onRequestOpen` lets a same-window return reopen the parent modal; `onDone`
 * closes it after a successful connect.
 */
export function useConnectFacebookPage(
  workspacePublicId: WorkspacePublicId | undefined,
  fetchWithAuth: FetchWithAuth,
  opts: { open: boolean; onRequestOpen: () => void; onDone: () => void },
) {
  const navigate = useNavigate();
  const { refreshChannels } = useWorkspace();
  const { token, clear: clearToken } = useFacebookLoginToken();
  const [phase, setPhase] = useState<'idle' | 'started' | 'picking' | 'submitting'>('idle');
  const [pages, setPages] = useState<FacebookPageOption[]>([]);
  // Once per relayed state: a StrictMode replay must not re-exchange the
  // short-lived token (Facebook invalidates the session as suspicious).
  const fetchedStateRef = useRef<string | null>(null);
  // State, not a ref: the handle arrives after the start mint resolves, and
  // the close watcher below must re-run once it does.
  const [popup, setPopup] = useState<Window | null>(null);

  // A relayed token with the connect intent in storage means this mount never
  // saw start() (same-window return, or a reconnect launched from the channel
  // detail page): treat it as started.
  const started = phase === 'started' || (phase === 'idle' && !!token && readFacebookConnectIntent());
  const step: FacebookConnectStep =
    phase === 'picking' || phase === 'submitting'
      ? phase
      : !started
        ? 'idle'
        : token
          ? 'loadingPages'
          : 'awaitingToken';

  const reset = useCallback(() => {
    setPhase('idle');
    setPopup(null);
    setPages([]);
    clearToken();
    clearFacebookConnectIntent();
  }, [clearToken]);

  const start = useCallback(async () => {
    if (!workspacePublicId) return;
    emit('facebook_connect_clicked', {});
    clearToken();
    setPages([]);
    setPhase('started');
    try {
      setPopup(await startFacebookConnect(workspacePublicId, fetchWithAuth));
    } catch (e) {
      captureException(e);
      toast.error('Could not start Facebook sign-in. Please try again.');
      reset();
    }
  }, [workspacePublicId, fetchWithAuth, clearToken, reset]);

  const pick = useCallback(
    async (page: FacebookPageOption) => {
      if (!token || !workspacePublicId) return;
      setPhase('submitting');
      try {
        const channel = await submitFacebookSignup(workspacePublicId, fetchWithAuth, {
          state: token.state,
          accessToken: token.accessToken,
          pageId: page.id,
        });
        toast.success('Facebook Page connected.');
        reset();
        opts.onDone();
        await refreshChannels(workspacePublicId);
        navigate(workspaceUrl(workspacePublicId, `channels/${channel.id}`));
      } catch (e) {
        captureException(e);
        toast.error(e instanceof AppError && e.userMessage ? e.userMessage : 'Could not connect this Page. Please try again.');
        setPhase('picking');
      }
    },
    [token, workspacePublicId, fetchWithAuth, reset, opts, refreshChannels, navigate],
  );

  // The dialog relays success only. A popup the customer cancelled or closed
  // would otherwise leave the flow in awaitingToken (Facebook row disabled)
  // until the modal is reopened. The token relay is async, so give a closed
  // popup a moment before treating it as a cancel.
  useEffect(() => {
    if (step !== 'awaitingToken' || !popup) return;
    let grace: ReturnType<typeof setTimeout> | undefined;
    const poll = setInterval(() => {
      if (!popup.closed) return;
      clearInterval(poll);
      grace = setTimeout(reset, 1500);
    }, 500);
    return () => {
      clearInterval(poll);
      if (grace) clearTimeout(grace);
    };
  }, [step, popup, reset]);

  // Token arrived: reopen the modal if the redirect closed it, list the Pages
  // once, then pick the only eligible one or show the picker.
  useEffect(() => {
    if (step !== 'loadingPages' || !token || !workspacePublicId) return;
    if (!opts.open) opts.onRequestOpen();
    if (fetchedStateRef.current === token.state) return;
    fetchedStateRef.current = token.state;
    void (async () => {
      try {
        const list = await fetchFacebookPages(workspacePublicId, fetchWithAuth, token);
        setPages(list);
        // A reconnect is for one Page only: pick it, or stop if the account
        // that signed in cannot reach it. Never offer the other Pages.
        const reconnectPageId = readFacebookReconnectPageId();
        if (reconnectPageId) {
          const target = list.find((p) => p.id === reconnectPageId);
          if (target && isEligibleFacebookPage(target)) {
            await pick(target);
            return;
          }
          toast.error('That Facebook account cannot manage this Page. Sign in with an admin of the Page to reconnect it.');
          reset();
          return;
        }
        const eligible = list.filter(isEligibleFacebookPage);
        if (eligible.length === 1) {
          await pick(eligible[0]);
          return;
        }
        setPhase('picking');
      } catch (e) {
        captureException(e);
        toast.error('Could not load your Facebook Pages. Please try again.');
        reset();
      }
    })();
  }, [step, token, workspacePublicId, fetchWithAuth, opts, pick, reset]);

  return { step, pages, start, pick, reset };
}
